/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import {
  accessAPI,
  Boton,
  Formulario,
  Sidebar,
  TablaFiltrable,
  Titulo,
  tituloBase,
  formatFecha,
  formatFechaHora,
  useIsMobile
} from "../../../shared/index.js";
import VisorCorreo from "../../correos/VisorCorreo.js";

/**
 * Historial de comunicación con el cliente. Cada conversación se arma con los
 * correos que llegan a la casilla del organismo; el asistente mantiene su
 * resumen, acuerdos y pendientes.
 *
 * Sin conversacionId en la URL muestra el listado; con conversacionId, el
 * detalle, y la flecha del título vuelve al listado.
 */
export default function ClienteComunicaciones() {
  const { clienteId, conversacionId } = useParams();
  const toast = useRef(null);
  const [razonsocial, setRazonsocial] = useState("");

  useEffect(() => {
    accessAPI(
      "GET",
      `admin/cliente/${clienteId}`,
      null,
      (respuesta) => {
        setRazonsocial(respuesta.razonsocial);
        document.title = `${respuesta.razonsocial} - Comunicaciones`;
      },
      () => {}
    );
    return () => (document.title = tituloBase());
  }, [clienteId]);

  if (!razonsocial) return <Cargando />;

  return (
    <>
      <Toast ref={toast} />
      {conversacionId ? (
        <DetalleConversacion
          key={conversacionId}
          clienteId={clienteId}
          conversacionId={conversacionId}
          razonsocial={razonsocial}
          toast={toast}
        />
      ) : (
        <ListadoConversaciones clienteId={clienteId} razonsocial={razonsocial} />
      )}
    </>
  );
}

function Cargando() {
  return (
    <div css={{ marginTop: "2em", display: "flex", flexDirection: "column", gap: "1em" }}>
      <Skeleton width="20rem" height="2rem" />
      <Skeleton width="100%" height="15rem" />
    </div>
  );
}

// Etiqueta de estado para el componente Titulo.
const etiquetaEstado = (estado) =>
  estado === "CERRADA" ? { texto: "Cerrada", neutro: true } : { texto: "Abierta", ok: true };

// Props del Tag de estado en la tabla de conversaciones.
const propsTagEstado = (estado) =>
  estado === "CERRADA"
    ? { value: "Cerrada", severity: "secondary" }
    : { value: "Abierta", severity: "success" };

function ListadoConversaciones({ clienteId, razonsocial }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [casilla, setCasilla] = useState("");
  const [conversaciones, setConversaciones] = useState(null);

  useEffect(() => {
    accessAPI(
      "GET",
      "admin/comunicacioncliente/casilla",
      null,
      (respuesta) => setCasilla(respuesta.direccion),
      () => {}
    );
    accessAPI(
      "GET",
      `admin/comunicacioncliente/cliente/${clienteId}`,
      null,
      (respuesta) => setConversaciones(respuesta),
      () => setConversaciones([])
    );
  }, [clienteId]);

  if (conversaciones === null) return <Cargando />;

  return (
    <>
      <Titulo subtitulo={casilla ? `Casilla del historial: ${casilla}` : undefined}>
        {razonsocial} - Comunicaciones
      </Titulo>
      <Card>
        <TablaFiltrable
          value={conversaciones}
          searchFields={["titulo", "resumen"]}
          nombreArchivo="comunicaciones"
          emptyMessage="Todavía no hay comunicaciones registradas con este cliente."
          size="small"
          onRowClick={(e) => {
            if (e.originalEvent.target.closest("button")) return;
            navigate(`/clientes/${clienteId}/comunicaciones/${e.data.id}`);
          }}
          rowClassName={() => "cursor-pointer"}
        >
          <Column
            header="Conversación"
            body={(c) => (
              <span css={{ fontWeight: 600 }}>
                {c.titulo}{" "}
                <Tag {...propsTagEstado(c.estado)} css={{ marginLeft: "0.5em", verticalAlign: "middle" }} />
              </span>
            )}
            exportValue={(c) => c.titulo}
          />
          <Column
            header="Última actividad"
            body={(c) => formatFecha(c.ultimaActividad)}
            exportValue={(c) => formatFecha(c.ultimaActividad)}
            style={{ whiteSpace: "nowrap" }}
          />
          {!isMobile && <Column header="Correos" field="cantidadCorreos" style={{ textAlign: "center" }} />}
          {!isMobile && (
            <Column
              header="Resumen"
              body={(c) => (
                <span
                  css={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}
                >
                  {c.resumen}
                </span>
              )}
              exportValue={(c) => c.resumen || ""}
            />
          )}
          {!isMobile && (
            <Column
              body={(c) => (
                <Boton texto negro uri={`/clientes/${clienteId}/comunicaciones/${c.id}`}>
                  <i className="pi pi-angle-right" />
                </Boton>
              )}
              style={{ width: "60px", textAlign: "center" }}
            />
          )}
        </TablaFiltrable>
      </Card>
    </>
  );
}

const nombreUsuario = (u) => [u?.nombre, u?.apellido].filter(Boolean).join(" ");

function DetalleConversacion({ clienteId, conversacionId, razonsocial, toast }) {
  const isMobile = useIsMobile();
  const [conversacion, setConversacion] = useState(null);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  const [correoAbierto, setCorreoAbierto] = useState(null);

  function cargar() {
    accessAPI(
      "GET",
      `admin/comunicacioncliente/conversacion/${conversacionId}`,
      null,
      (respuesta) => setConversacion(respuesta),
      () =>
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "No se pudo cargar la conversación"
        })
    );
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversacionId]);

  function guardar(formData) {
    setGuardando(true);
    accessAPI(
      "PUT",
      `admin/comunicacioncliente/conversacion/${conversacionId}`,
      { titulo: formData.titulo, resumen: formData.resumen || "" },
      () => {
        setGuardando(false);
        setEditando(false);
        cargar();
      },
      (respuesta) => {
        setGuardando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.error || "No se pudo guardar"
        });
      }
    );
  }

  function cambiarEstado() {
    setCambiandoEstado(true);
    accessAPI(
      "PUT",
      `admin/comunicacioncliente/conversacion/${conversacionId}/estado`,
      { estado: conversacion.estado === "ABIERTA" ? "CERRADA" : "ABIERTA" },
      () => {
        setCambiandoEstado(false);
        cargar();
      },
      () => {
        setCambiandoEstado(false);
        toast.current?.show({ severity: "error", summary: "Error", detail: "No se pudo cambiar el estado" });
      }
    );
  }

  if (!conversacion) return <Cargando />;

  const formularioEdicion = {
    secciones: [
      {
        nombre: "Editar resumen",
        campos: [
          {
            nombre: "titulo",
            tipoitemformularioid: 1,
            etiqueta: "Título",
            requerido: true,
            respuesta: conversacion.titulo
          },
          {
            nombre: "resumen",
            tipoitemformularioid: 11,
            etiqueta: "Resumen",
            respuesta: conversacion.resumen || ""
          }
        ]
      }
    ],
    botones: [{ etiqueta: "Guardar cambios", callback: guardar, validar: true }]
  };

  const acuerdos = Array.isArray(conversacion.acuerdos) ? conversacion.acuerdos : [];
  const pendientes = Array.isArray(conversacion.pendientes) ? conversacion.pendientes : [];

  return (
    <>
      <Titulo
        urlVolver={`/clientes/${clienteId}/comunicaciones`}
        subtitulo={razonsocial}
        etiquetas={[etiquetaEstado(conversacion.estado)]}
        botones={
          conversacion.estado === "ABIERTA" ? (
            <Boton rojo transparente loader={cambiandoEstado} callback={cambiarEstado}>
              Cerrar conversación
            </Boton>
          ) : (
            <Boton transparente loader={cambiandoEstado} callback={cambiarEstado}>
              Reabrir conversación
            </Boton>
          )
        }
      >
        {conversacion.titulo}
      </Titulo>

      <div css={{ display: "flex", flexDirection: "column", gap: "1em" }}>
        <Card>
          <Titulo
            botones={
              <Boton texto negro chico callback={() => setEditando(true)}>
                <i className="pi pi-pencil" />
              </Boton>
            }
          >
            Resumen
          </Titulo>
          <p css={{ whiteSpace: "pre-wrap", marginTop: 0 }}>
            {conversacion.resumen || "Todavía sin resumen."}
          </p>
          {conversacion.cerradaPor && (
            <div css={{ fontSize: "0.8em", opacity: 0.7 }}>
              Cerrada por {nombreUsuario(conversacion.cerradaPor)} el{" "}
              {formatFechaHora(conversacion.fechaCierre)}
            </div>
          )}
        </Card>

        <div
          css={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(18em, 1fr))", gap: "1em" }}
        >
          <Card>
            <Titulo>Pendientes</Titulo>
            {pendientes.length === 0 ? (
              "Sin pendientes."
            ) : (
              <ul css={{ margin: 0, paddingLeft: "1.2em" }}>
                {pendientes.map((p, i) => (
                  <li key={i}>
                    {p.texto}
                    {(p.responsable || p.fecha) && (
                      <span css={{ opacity: 0.7 }}>
                        {" "}
                        ({[p.responsable, p.fecha].filter(Boolean).join(", ")})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card>
            <Titulo>Acuerdos</Titulo>
            {acuerdos.length === 0 ? (
              "Sin acuerdos."
            ) : (
              <ul css={{ margin: 0, paddingLeft: "1.2em" }}>
                {acuerdos.map((a, i) => (
                  <li key={i}>
                    {a.texto}
                    {a.fecha && <span css={{ opacity: 0.7 }}> ({a.fecha})</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card>
          <Titulo>Correos</Titulo>
          <TablaFiltrable
            conFiltros={false}
            value={conversacion.correos}
            size="small"
            onRowClick={(e) => {
              if (e.originalEvent.target.closest("button")) return;
              setCorreoAbierto(e.data.id);
            }}
            rowClassName={() => "cursor-pointer"}
          >
            <Column
              header="Fecha"
              body={(c) => formatFechaHora(c.fecha || c.fechaRecepcion)}
              style={{ whiteSpace: "nowrap" }}
            />
            <Column header="De" field="de" />
            {!isMobile && <Column header="Asunto" body={(c) => c.asunto || "(sin asunto)"} />}
            {!isMobile && (
              <Column
                header="Novedades"
                body={(c) =>
                  c.estado === "ERROR" ? (
                    <span css={{ color: "var(--rojo)" }}>No se pudo procesar: {c.errorProcesamiento}</span>
                  ) : (
                    c.novedades
                  )
                }
              />
            )}
            {!isMobile && (
              <Column
                header="Adjuntos"
                body={(c) => c.adjuntos.length || ""}
                style={{ textAlign: "center" }}
              />
            )}
            {!isMobile && (
              <Column
                body={(c) => (
                  <Boton texto negro callback={() => setCorreoAbierto(c.id)}>
                    <i className="pi pi-envelope" />
                  </Boton>
                )}
                style={{ width: "60px", textAlign: "center" }}
              />
            )}
          </TablaFiltrable>
        </Card>
      </div>

      <Sidebar
        visible={editando}
        position="right"
        onHide={() => setEditando(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        {editando && <Formulario config={formularioEdicion} loader={guardando} />}
      </Sidebar>

      <Sidebar
        visible={!!correoAbierto}
        position="right"
        onHide={() => setCorreoAbierto(null)}
        style={{ width: "min(60rem, 100vw)" }}
      >
        {correoAbierto && <VisorCorreo correoId={correoAbierto} toast={toast} />}
      </Sidebar>
    </>
  );
}
